package com.evoapps.evoshift.ui.roster

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Event
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.usecase.specialdays.LocalEventProblem
import com.evoapps.evoshift.domain.usecase.specialdays.SaveLocalEventUseCase
import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.ui.util.dayAndDate
import com.evoapps.evoshift.ui.util.dayRangeText
import com.evoapps.evoshift.viewmodels.LocalEventDialogState
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.EvoCheckboxRow
import com.evoapps.evoshift.widgets.EvoDateRangeField
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoListRow
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.normalisedDate
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.delete
import evoshift.composeapp.generated.resources.local_event_also_in
import evoshift.composeapp.generated.resources.local_event_edit_named
import evoshift.composeapp.generated.resources.local_event_error_date
import evoshift.composeapp.generated.resources.local_event_error_long
import evoshift.composeapp.generated.resources.local_event_error_name
import evoshift.composeapp.generated.resources.local_event_error_name_long
import evoshift.composeapp.generated.resources.local_event_error_order
import evoshift.composeapp.generated.resources.local_event_from
import evoshift.composeapp.generated.resources.local_event_name
import evoshift.composeapp.generated.resources.local_event_name_hint
import evoshift.composeapp.generated.resources.local_event_on_this_day
import evoshift.composeapp.generated.resources.local_event_title_edit
import evoshift.composeapp.generated.resources.local_event_title_new
import evoshift.composeapp.generated.resources.local_event_to
import evoshift.composeapp.generated.resources.local_event_yearly
import evoshift.composeapp.generated.resources.local_event_yearly_hint
import evoshift.composeapp.generated.resources.save
import org.jetbrains.compose.resources.stringResource

/** SH-227: test tags of the event dialog. */
object LocalEventDialogTags {
    const val NAME = "local_event_name"
    const val YEARLY = "local_event_yearly"
    fun alsoIn(workplaceId: String) = "local_event_also_$workplaceId"
    fun dayEvent(eventId: String) = "local_event_day_$eventId"
}

/**
 * SH-227: the one small dialog of local events: a name, the dates (the day it was opened on, by
 * default one day), "Every year" and, for a new event of someone with several workplaces, "Also
 * at" to add it there too. A new event's dialog lists the day's events to open one instead; an
 * event's dialog can delete it. [onSave] answers what is wrong, or null when it was saved.
 */
@Composable
fun LocalEventDialog(
    state: LocalEventDialogState,
    onSave: (name: String, from: kotlinx.datetime.LocalDate, to: kotlinx.datetime.LocalDate, yearly: Boolean, alsoIn: Set<String>) -> LocalEventProblem?,
    onEdit: (eventId: String) -> Unit,
    onDelete: () -> Unit,
    onDismiss: () -> Unit
) {
    val editing = state.editing
    var name by remember(state) { mutableStateOf(editing?.name.orEmpty()) }
    var from by remember(state) { mutableStateOf((editing?.from ?: state.date).toString()) }
    var to by remember(state) { mutableStateOf((editing?.to ?: state.date).toString()) }
    var yearly by remember(state) { mutableStateOf(editing?.yearly ?: false) }
    var alsoIn by remember(state) { mutableStateOf(emptySet<String>()) }
    var showErrors by remember(state) { mutableStateOf(false) }
    var problem by remember(state) { mutableStateOf<LocalEventProblem?>(null) }

    val today = AppClock.today()
    val start = normalisedDate(from, today)
    val end = normalisedDate(to, today)
    val live = SaveLocalEventUseCase.problemOf(name, start, end)
    val shown = if (showErrors) live ?: problem else null
    val dateMissing = stringResource(Res.string.local_event_error_date)

    EvoDialog(
        title = stringResource(if (editing == null) Res.string.local_event_title_new else Res.string.local_event_title_edit),
        subtitle = dayAndDate(state.date),
        icon = Icons.Default.Event,
        onDismiss = onDismiss,
        confirm = DialogAction(stringResource(Res.string.save), {
            showErrors = true
            if (start != null && end != null) problem = onSave(name, start, end, yearly, alsoIn)
        }),
        neutral = if (editing != null) DialogAction(stringResource(Res.string.delete), onDelete, destructive = true) else null
    ) {
        // A new event on a day that has some: open one of them instead.
        if (editing == null && state.dayEvents.isNotEmpty()) {
            DialogSection(stringResource(Res.string.local_event_on_this_day)) {
                state.dayEvents.forEach { event ->
                    EvoListRow(
                        headline = stringResource(Res.string.local_event_edit_named, event.name),
                        supporting = dayRangeText(event.from, event.to),
                        leadingIcon = Icons.Default.Edit,
                        onClick = { onEdit(event.id) },
                        modifier = Modifier.testTag(LocalEventDialogTags.dayEvent(event.id))
                    )
                }
            }
        }
        DialogSection(null) {
            EvoTextField(
                value = name,
                onValueChange = { name = it.take(80) },
                label = stringResource(Res.string.local_event_name),
                placeholder = stringResource(Res.string.local_event_name_hint),
                error = when (shown) {
                    LocalEventProblem.NAME_MISSING -> stringResource(Res.string.local_event_error_name)
                    LocalEventProblem.NAME_TOO_LONG -> stringResource(Res.string.local_event_error_name_long)
                    else -> null
                },
                supportingText = "${name.trim().length}/${com.evoapps.evoshift.domain.model.LocalEvent.MAX_NAME_LENGTH}",
                modifier = Modifier.testTag(LocalEventDialogTags.NAME)
            )
            EvoDateRangeField(
                start = from,
                end = to,
                onStartChange = { new ->
                    from = new
                    // Moving the start past the end moves the end along (a one-day event stays one day).
                    val s = normalisedDate(new, today)
                    val e = normalisedDate(to, today)
                    if (s != null && (e == null || e < s)) to = s.toString()
                },
                onEndChange = { to = it },
                startLabel = stringResource(Res.string.local_event_from),
                endLabel = stringResource(Res.string.local_event_to),
                showErrors = showErrors,
                startError = if (showErrors && start == null) dateMissing else null,
                endError = when {
                    showErrors && end == null -> dateMissing
                    shown == LocalEventProblem.ENDS_BEFORE_START -> stringResource(Res.string.local_event_error_order)
                    shown == LocalEventProblem.TOO_LONG -> stringResource(Res.string.local_event_error_long)
                    else -> null
                }
            )
            EvoCheckboxRow(
                checked = yearly,
                onCheckedChange = { yearly = it },
                label = stringResource(Res.string.local_event_yearly),
                supporting = stringResource(Res.string.local_event_yearly_hint),
                modifier = Modifier.testTag(LocalEventDialogTags.YEARLY)
            )
        }
        // Owners (and managers) of several workplaces: the same event there too, in one go.
        if (editing == null && state.otherWorkplaces.isNotEmpty()) {
            DialogSection(stringResource(Res.string.local_event_also_in)) {
                Column(verticalArrangement = Arrangement.spacedBy(0.dp)) {
                    state.otherWorkplaces.forEach { workplace ->
                        EvoCheckboxRow(
                            checked = workplace.id in alsoIn,
                            onCheckedChange = { on -> alsoIn = if (on) alsoIn + workplace.id else alsoIn - workplace.id },
                            label = workplace.name,
                            modifier = Modifier.testTag(LocalEventDialogTags.alsoIn(workplace.id))
                        )
                    }
                }
            }
        }
    }
}
